import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, FormHelperText } from "@mui/material";
import { Controller, Control, FieldValues, Path, FieldError } from "react-hook-form";

type OptionType<T> = { label: string; value: T };

interface InputMultiSelectProps<K extends FieldValues, T extends string | number> {
  name: Path<K>;
  control: Control<K>;
  error?: FieldError;
  options: OptionType<T>[];
  label?: string | React.ReactNode;
  disabled?: boolean;
  readonly?: boolean;
  onChange?: (selectedValues: T[]) => void;
}

export default function InputMultiSelect<K extends FieldValues, T extends string | number>({
  name,
  control,
  error,
  options,
  label,
  disabled,
  readonly,
  onChange
}: InputMultiSelectProps<K, T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl fullWidth margin="dense" variant="standard" error={!!error} disabled={disabled || readonly}>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={field.value ?? []}
            onChange={(event: SelectChangeEvent<T[]>) => {
              const newValue = event.target.value as T[];
              field.onChange(newValue);
              onChange?.(newValue);
            }}
            readOnly={readonly}
            renderValue={(selected) => selected.map((val) => options.find(opt => opt.value === val)?.label).join(', ')}
          >
            {options.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
}
